import React, { Fragment, useMemo } from 'react';
import { Menu, Transition } from '@headlessui/react';
import { FaChevronDown } from 'react-icons/fa6';
import { Trade } from 'utils/shared/trades/types';
import classNames from 'styles/utils/classNames';
import { formatExpirationDate, getDaysToExpiration } from 'utils/shared/time/formatExpirationDate';

interface ExpirationDateDropdownProps {
  trades: Trade[];
  selectedExpirations: string[];
  onSelectionChange: (dates: string[]) => void;
}

interface ExpirationOption {
  date: string;
  etfCount: number;
  stockCount: number;
}

export default function ExpirationDateDropdown({
  trades,
  selectedExpirations,
  onSelectionChange,
}: ExpirationDateDropdownProps) {
  const expirationOptions = useMemo<ExpirationOption[]>(() => {
    const dateMap = new Map<string, { etf: number; stock: number }>();

    trades.forEach((trade) => {
      if (trade.expiryString) {
        const entry = dateMap.get(trade.expiryString) || { etf: 0, stock: 0 };
        if (trade.tickerDetails?.sector === 'ETF') {
          entry.etf++;
        } else {
          entry.stock++;
        }
        dateMap.set(trade.expiryString, entry);
      }
    });

    return (
      Array.from(dateMap.entries())
        .map(([date, counts]) => ({ date, etfCount: counts.etf, stockCount: counts.stock }))
        // Never show expired expirations — hide anything before today.
        .filter((option) => getDaysToExpiration(option.date) >= 0)
        .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    );
  }, [trades]);

  const handleToggle = (date: string, event: React.MouseEvent) => {
    event.preventDefault();
    event.stopPropagation();

    if (selectedExpirations.includes(date)) {
      onSelectionChange(selectedExpirations.filter((d) => d !== date));
    } else {
      onSelectionChange([...selectedExpirations, date]);
    }
  };

  const handleSelectAll = () => {
    onSelectionChange([]);
  };

  const selectedCount = selectedExpirations.length;
  const isAllSelected = selectedCount === 0;

  return (
    <Menu as="div" className="relative flex w-full grow text-left">
      <Menu.Button className="flex w-full grow items-center justify-between rounded border border-palette-border bg-palette-black-1 py-1 pl-2 pr-2 text-sm">
        {isAllSelected
          ? 'Expirations'
          : selectedCount === 1
          ? formatExpirationDate(selectedExpirations[0])
          : `${selectedCount} dates`}
        <FaChevronDown className="h-2.5 w-2.5 text-gray-400" aria-hidden="true" />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 z-10 mt-2 max-h-80 w-80 origin-top-right divide-y divide-gray-100 overflow-auto rounded-md border border-palette-border bg-palette-black-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
          <div className="py-1">
            <Menu.Item>
              {({ active }) => (
                <button
                  className={classNames(
                    isAllSelected ? 'bg-blue-500 text-white' : '',
                    active ? 'bg-brand-primary text-white' : 'text-white',
                    'flex w-full items-center justify-between px-4 py-2 text-left text-sm',
                  )}
                  onClick={handleSelectAll}
                >
                  <span className="font-semibold">All</span>
                  <span className="ml-4 text-gray-400">{trades.length}</span>
                </button>
              )}
            </Menu.Item>
          </div>
          <div className="py-1">
            {expirationOptions.map(({ date, etfCount, stockCount }) => {
              const isSelected = selectedExpirations.includes(date);
              return (
                <Menu.Item key={date}>
                  {({ active }) => (
                    <div
                      className={classNames(
                        isSelected ? 'bg-blue-500 text-white' : '',
                        active ? 'bg-brand-primary text-white' : 'text-white',
                        'flex w-full cursor-pointer items-center px-4 py-2 text-xs',
                      )}
                      onClick={(e) => handleToggle(date, e)}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        readOnly
                        className="h-3.5 w-3.5 shrink-0 cursor-pointer rounded border-gray-300"
                      />
                      <span className="ml-2">{formatExpirationDate(date)}</span>
                      <span className="ml-2 text-gray-400">
                        {etfCount > 0 && `${etfCount} ETFs`}
                        {etfCount > 0 && stockCount > 0 && ' '}
                        {stockCount > 0 && `${stockCount} Stocks`}
                      </span>
                    </div>
                  )}
                </Menu.Item>
              );
            })}
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}
